import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Phone,
  LocalHospital,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Client, Service } from '../types';
import { clientAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface RowProps {
  client: Client;
  providerId: number;
}

// Helper function to format date without timezone conversion issues
// Parses date string directly to avoid timezone conversion
const formatDateOnly = (dateString: string | undefined): string => {
  if (!dateString) return 'Invalid date';
  try {
    // Parse the date string directly to avoid timezone conversion
    // Handle ISO format: "2024-01-15T00:00:00" or "2024-01-15T00:00:00Z" or "2024-01-15"
    const datePart = dateString.split('T')[0]; // Get YYYY-MM-DD part
    const [year, month, day] = datePart.split('-').map(Number);
    
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return 'Invalid date';
    }
    
    // Create a date object using local timezone to avoid conversion issues
    const dateObj = new Date(year, month - 1, day);
    return format(dateObj, 'MMM dd, yyyy');
  } catch (error) {
    return 'Invalid date';
  }
};

const Row: React.FC<RowProps> = ({ client, providerId }) => {
  const [open, setOpen] = useState(false);
  const [authorizedServices, setAuthorizedServices] = useState<Service[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);

  const fetchAuthorizedServices = useCallback(async () => {
    if (!open || authorizedServices.length > 0) return;
    
    setLoadingServices(true);
    try {
      const services = await clientAPI.getAuthorizedServices(client.client_id, providerId);
      setAuthorizedServices(services);
    } catch (error) {
      console.error('Error fetching authorized services:', error);
    } finally {
      setLoadingServices(false);
    }
  }, [open, authorizedServices.length, client.client_id, providerId]);

  useEffect(() => {
    fetchAuthorizedServices();
  }, [open, fetchAuthorizedServices]);

  return (
    <>
      <TableRow sx={{ 
        '& > *': { borderBottom: 'unset' },
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        '&:hover': {
          backgroundColor: 'rgba(45, 75, 115, 0.1)',
        }
      }}>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            sx={{ color: '#2D4B73' }}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row" sx={{ fontWeight: 600, color: '#2D4B73' }}>
          {client.first_name} {client.last_name}
        </TableCell>
        <TableCell sx={{ color: '#2D4B73' }}>
          {formatDateOnly(client.date_of_birth)}
        </TableCell>
        <TableCell sx={{ color: '#2D4B73' }}>{client.email}</TableCell>
        <TableCell sx={{ color: '#2D4B73' }}>{client.phone}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.3)', borderRadius: 8 }}>
              <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 600, color: '#2D4B73' }}>
                Additional Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Address
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#2D4B73' }}>
                    {client.address_line1} {client.address_line2}
                    {client.city && `, ${client.city}`}
                    {client.state && `, ${client.state}`}
                    {client.zip_code && ` ${client.zip_code}`}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Emergency Contact
                  </Typography>
                  {client.emergency_contact_name ? (
                    <>
                      <Typography variant="body2" sx={{ color: '#2D4B73' }}>
                        {client.emergency_contact_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Phone sx={{ fontSize: 14, mr: 0.5, color: '#2D4B73' }} />
                        {client.emergency_contact_phone}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#2D4B73', fontStyle: 'italic' }}>
                      No emergency contact information
                    </Typography>
                  )}
                </Box>
              </Box>
              
              {/* Authorized Services Section */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 600, color: '#2D4B73', display: 'flex', alignItems: 'center' }}>
                  <LocalHospital sx={{ mr: 1, color: '#2D4B73' }} />
                  Authorized Services
                </Typography>
                {loadingServices ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : authorizedServices.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {authorizedServices.map((service) => (
                      <Chip
                        key={service.service_id}
                        label={[service.code, service.service_unit, service.program, service.category].filter(Boolean).join(' | ') || service.service_name || `Service ${service.service_id}`}
                        variant="outlined"
                        sx={{
                          borderColor: '#2D4B73',
                          color: '#2D4B73',
                          '&:hover': {
                            backgroundColor: 'rgba(45, 75, 115, 0.1)',
                          }
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#2D4B73', fontStyle: 'italic' }}>
                    No authorized services found
                  </Typography>
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const ClientsList: React.FC = () => {
  const { provider } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        console.log('ClientsList: Provider object:', provider);
        console.log('ClientsList: Provider ID:', provider?.provider_id);
        console.log('ClientsList: Provider ID type:', typeof provider?.provider_id);
        // Only fetch clients for the current provider
        const providerId = provider?.provider_id;
        console.log('ClientsList: Final providerId being passed:', providerId);

        if (!providerId) {
          throw new Error('No provider ID available');
        }
        
        const data = await clientAPI.getClients(providerId);
        console.log('ClientsList: Fetched clients:', data);
        console.log('ClientsList: Number of clients:', data.length);
        setClients(data);
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [provider]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', color: '#2D4B73' }}>
        My Clients
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, color: '#2D4B73' }}>
        View information about your clients. This data is read-only.
      </Typography>

      <Paper sx={{ 
        mt: 3,
        borderRadius: 0,
        backgroundColor: '#ffffff',
        border: '1px solid rgba(45, 75, 115, 0.2)',
        boxShadow: '0 8px 32px 0 rgba(45, 75, 115, 0.15)',
      }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#2D4B73' }}>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }} />
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>Date of Birth</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#ffffff' }}>Phone</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.length > 0 ? (
                clients.map((client) => (
                  <Row key={client.client_id} client={client} providerId={provider?.provider_id || 0} />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" sx={{ color: '#2D4B73', fontStyle: 'italic' }}>
                      No clients found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ClientsList;
